import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/api";
import SearchSelect from "@/pages/SearchSelect";
import { DatePicker } from "@/components/Planning/components/DatePicker";
import { useVanoCodes } from "@/context/VanoCodesContext";
import { getUserInfo } from "@/user";
import { formatDate, formatDateToYMD } from "@/utils/dateFormat";
import { apiErrorMessage, displayValue, trialCount } from "@/utils/massUpdate";
import { MassPreviewDialog, MassResult, MassError } from "./MassUpdateReview";
import CharCount from "./CharCount";
import CompletedBySelect from "./CompletedBySelect";
import { DIARY_MAX_LENGTH } from "@/utils/trialFieldLimits";

/*
 * Mass complete on /trials: one task - an activity (e.g. A13 Border spray), a visit (Visit 03) or
 * an application (Application B) - marked as done on the selected trials, by the chosen user (by
 * default the signed-in user; as Complete task). Preview first - per trial whether the task is completed, created (only
 * an activity), already done or skipped - then complete the trials kept.
 */

// Visits are numbered 00, 01, ... and applications lettered A-Z, as the API numbers new ones
const VISITS = Array.from({ length: 100 }, (_, i) => String(i).padStart(2, "0")).map((no) => ({ value: no, label: `Visit ${no}` }));
const APPLICATIONS = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map((letter) => ({ value: letter, label: `Application ${letter}` }));

const TASK_TYPES = [
  ["activity", "Activity", "Choose an activity, e.g. A13 Borders Sprayed"],
  ["visit", "Visit", "Choose a visit, e.g. Visit 03"],
  ["application", "Application", "Choose an application, e.g. Application B"],
];

export default function MassCompleteTasks({ selectedTrialIds = [], setSelectedTrialIds, refreshTrials }) {
  const vanoCodes = useVanoCodes();
  const activities = useMemo(
    () => vanoCodes.filter((entry) => entry.visible).map((entry) => ({ value: entry.vano, label: `${entry.vano} ${entry.label ?? entry.task}` })),
    [vanoCodes]
  );

  const [type, setType] = useState("activity");
  const [vano, setVano] = useState(null);
  const [doneDate, setDoneDate] = useState(() => new Date());
  const [bbch, setBbch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null); // { request, activity, rows }
  const [result, setResult] = useState(null);
  const retryRef = useRef(null);

  // Who did the tasks (customer point 61): one user for all trials, by default the signed-in user, who the API
  // records as who registered them
  const [completedBy, setCompletedBy] = useState("");
  useEffect(() => {
    let ignore = false;
    getUserInfo().then((user) => {
      if (ignore) return;
      setCompletedBy((current) => current || (user?.id ?? ""));
    });
    return () => {
      ignore = true;
    };
  }, []);

  // BBCH is free text, as on the diary entry (e.g. "31-32", "at seeding"); the input stops at its limit
  const canPreview = selectedTrialIds.length > 0 && vano && doneDate && !loading;

  async function run(action, fallback) {
    retryRef.current = () => run(action, fallback);
    setLoading(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(apiErrorMessage(e, fallback));
    } finally {
      setLoading(false);
    }
  }

  function handlePreview() {
    const request = {
      type, vano, donedate: formatDateToYMD(doneDate),
      ...(bbch.trim() !== "" ? { bbch: bbch.trim() } : {}),
      ...(completedBy ? { completed_by: completedBy } : {}),
    };
    run(async () => {
      setResult(null);
      const { data } = await api.post("/trials/masscomplete/preview", { ids: selectedTrialIds, ...request });
      setPreview({ request, task: data.task, rows: data.trials });
    }, "Failed to preview the tasks.");
  }

  function handleApply(ids) {
    const { request } = preview;
    run(async () => {
      try {
        const { data } = await api.post("/trials/masscomplete", { ids, ...request });
        setResult(data);
        setSelectedTrialIds?.([]);
      } finally {
        setPreview(null);
      }
      if (typeof refreshTrials === "function") await refreshTrials();
    }, "Failed to complete the tasks.");
  }

  const fieldLabel = (field) => vanoCodes.find((entry) => entry.field === field)?.label ?? field;

  const describeRow = (row) => {
    if (row.reason) return row.reason;
    const flags = row.changes.filter((c) => c.field !== "donedate" && c.field !== "bbch");
    return (
      <ul className="space-y-0.5">
        {row.outcome === "create" && <li className="text-blue-700 dark:text-blue-300">Task will be created</li>}
        {flags.map((c) => (
          <li key={c.field}>
            <span className="font-medium">{fieldLabel(c.field)}:</span> {displayValue(c.field, c.old)} → {displayValue(c.field, c.new)}
          </li>
        ))}
        <li>Done date {formatDate(preview.request.donedate)}{preview.request.bbch ? `, BBCH ${preview.request.bbch}` : ""}</li>
      </ul>
    );
  };

  const resultSummary = (data) => {
    const done = data.updated + data.created;
    return `Completed ${trialCount(done)}${data.created ? ` (task created on ${data.created})` : ""}.`;
  };

  return (
    <div className="dark:text-gray-100" data-testid="mass-complete">
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-3">
        <div className="space-y-1">
          <span className="inline-block font-medium">Task</span>
          <div className="flex gap-2">
            <select
              aria-label="Task type"
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setVano(null);
              }}
              className="rounded-md border border-gray-300 bg-white px-2 py-1.5 dark:border-gray-600 dark:bg-gray-700"
            >
              {TASK_TYPES.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <SearchSelect
              key={type}
              className="flex-1"
              options={type === "visit" ? VISITS : type === "application" ? APPLICATIONS : activities}
              value={vano}
              onChange={(option) => setVano(option?.value ?? null)}
              placeholder={TASK_TYPES.find(([value]) => value === type)[2]}
            />
          </div>
        </div>
        <div className="space-y-1">
          <span className="inline-block font-medium">Done date</span>
          <DatePicker selected={doneDate} onChange={setDoneDate} label="Done date" />
        </div>
        <div className="space-y-1">
          <label className="inline-block font-medium" htmlFor="massBbch">
            BBCH (optional)
          </label>
          <input
            id="massBbch"
            value={bbch}
            onChange={(e) => setBbch(e.target.value)}
            maxLength={DIARY_MAX_LENGTH.bbch}
            placeholder='e.g. "31-32" or "at seeding"'
            className="block w-full rounded-lg border border-gray-200 px-3 py-2 leading-6 placeholder-gray-500 focus:border-emerald-500 focus:ring-3 focus:ring-emerald-500/50 dark:border-gray-600 dark:bg-gray-800 dark:placeholder-gray-400"
          />
          <CharCount value={bbch} max={DIARY_MAX_LENGTH.bbch} />
        </div>
      </div>
      <div className="mt-3 max-w-sm space-y-1 text-sm" data-testid="mass-completed-by">
        <label className="inline-block font-medium" htmlFor="mass-completed-by-select">Completed by</label>
        <CompletedBySelect id="mass-completed-by-select" value={completedBy} onChange={setCompletedBy} />
      </div>

      <div className="flex justify-end pt-4">
        <button
          type="button"
          onClick={handlePreview}
          disabled={!canPreview}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-700 bg-emerald-700 px-3 py-2 text-sm font-semibold leading-5 text-white hover:border-emerald-600 hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Preview tasks
        </button>
      </div>

      {preview && (
        <MassPreviewDialog
          title={`You are completing ${preview.task.label} on ${trialCount(preview.rows.length)}`}
          rows={preview.rows}
          outcomes={{
            update: { label: "Will be completed", tone: "green" },
            create: { label: "Task will be created", tone: "blue" },
            already_done: { label: "Already done", tone: "gray" },
            blocked: { label: "Skipped", tone: "amber" },
          }}
          applicable={["update", "create"]}
          describe={describeRow}
          confirmText="Complete on"
          busy={loading}
          onConfirm={handleApply}
          onCancel={() => setPreview(null)}
        />
      )}
      {error && <MassError message={error} onRetry={() => retryRef.current?.()} disabled={loading} />}
      {result && (
        <MassResult
          summary={resultSummary(result)}
          skipped={result.skipped}
          note="Stage is not changed automatically."
          onClose={() => setResult(null)}
        />
      )}
    </div>
  );
}
